import classNames from 'classnames';
import * as React from 'react';
import validator from 'validator';
import { DynamicResultTable } from '../../dynamic-result-table';
import { FormattedDate } from '../../formattedV2';
import { DEFAULT_ROWS_PER_PAGE, NOT_AVAILABLE, NO_DETAILS_TO_SHOW } from '@atlassian/cssxp-utils-entitlement';
import './audit-notes-v2.scss';
import { DATE_TIME_FORMAT } from '@atlassian/cssxp-typescommon';
import { CustomPanel } from '../../custom-panel';
import { useQuery } from '@apollo/client';
import { generatedTypesNew } from '@css-xp/graphtypes';
import { fetchAuditNotesForEntity } from './queries/fetch-audit-notes';

export interface AuditNotesV2TxaProps {
  id: string;
  entityName: string;
  label?: string;
  idText?: string;
}

interface SearchParamsArgs {
  id: string;
  idText?: string;
}

const headers = ['Date', 'Update', 'Details', 'Actioned by'];

const wrapInCell = (node: React.ReactElement | string) => {
  return <div className="audit-note-cell">{node}</div>;
};

const makeHead = () => {
  const cells = headers?.map((header) => {
    return {
      content: wrapInCell(header),
    };
  });
  return {
    cells,
  };
};

const makeColumns = (auditNotes: generatedTypesNew.AuditNote[] | undefined) => {
  return auditNotes?.map((auditNote, index) => {
    const { created, body, author, contactKey } = auditNote;
    const action = body?.split('-');
    const update = action?.[0];
    let updatedBodyValue = update;
    if (update?.includes('Billing_Admin_Updated')) {
      updatedBodyValue = update.replace('Updated', 'Added');
    }
    if (update?.includes('BILLING_ADMIN_UPDATED')) {
      updatedBodyValue = update.replace('UPDATED', 'ADDED');
    }
    let detail;
    if (action && action?.length > 1) {
      detail = body?.slice(body.indexOf('-')).slice(1);
    }
    return {
      key: `audit-note-v2-${index}`,
      cells: [
        {
          content: wrapInCell(<FormattedDate date={created?.toString()} dateFormat={DATE_TIME_FORMAT} />),
        },
        {
          content: wrapInCell(<span>{updatedBodyValue ?? NOT_AVAILABLE}</span>),
        },
        {
          content: wrapInCell(<span>{detail ? detail : contactKey ?? NOT_AVAILABLE}</span>),
        },
        {
          content: wrapInCell(
            <div
              className={classNames({
                email: author && validator.isEmail(author ?? ''),
              })}
            >
              {author ? author : created && update ? 'System' : NOT_AVAILABLE}
            </div>
          ),
        },
      ],
    };
  });
};

const getEmptyStateProps = (error = '') => {
  const emptyStateProps = {
    header: error ? 'Oh no! Something went wrong, please try again ' : NO_DETAILS_TO_SHOW,
    description: error,
  };
  return emptyStateProps;
};

const defaultSortOptions = {
  sortFields: ['created', 'id'],
  sortFieldsDirections: [generatedTypesNew.SortDirection.DESC, generatedTypesNew.SortDirection.DESC],
};

const defaultAuditNotesSearchOptions: generatedTypesNew.FetchAuditNotesForEntityQueryVariables['searchOptions'] = {
  first: DEFAULT_ROWS_PER_PAGE,
  sort: defaultSortOptions,
};

export const AuditNotesTxaV2 = ({ id, entityName, label = 'Audit trail', idText }: AuditNotesV2TxaProps) => {
  const [rowsPerPage, setRowsPerPage] = React.useState<number>(DEFAULT_ROWS_PER_PAGE);
  const [currentPage, setCurrentPage] = React.useState<number>(1);

  let panelPageName = '';

  if (entityName === 'transactionAccount') {
    panelPageName = 'for this billing account';
  } else if (entityName === 'refund') {
    panelPageName = 'for this credit note';
  } else if (entityName === 'invoice') {
    panelPageName = 'for this invoice';
  } else if (entityName === 'cloudsite') {
    panelPageName = 'for this cloud site';
  }

  const panelHeading = 'audit trail' + ' ' + panelPageName;

  const buildSearchParams = ({
    id,
    idText,
  }: SearchParamsArgs): generatedTypesNew.CommerceNoteSearchParams | undefined => {
    const searchAndParams = [{ transactionAccountKey: id }];
    if (idText) {
      return {
        and: [...searchAndParams, { idText }],
      };
    }
    return {
      and: [...searchAndParams],
    };
  };

  const searchParams = buildSearchParams({ id, idText });

  const { data, loading, error, refetch } = useQuery<
    generatedTypesNew.FetchAuditNotesForEntityQuery,
    generatedTypesNew.FetchAuditNotesForEntityQueryVariables
  >(fetchAuditNotesForEntity, {
    variables: {
      searchParams,
      searchOptions: defaultAuditNotesSearchOptions,
    },
    displayName: 'fetchAuditNotesByEntityId',
    notifyOnNetworkStatusChange: true,
  });

  const queryResults = data?.auditNotes;
  const auditLogs = queryResults?.nodes as generatedTypesNew.AuditNote[];

  const totalCount = queryResults?.totalCount ?? 0;
  const columns = error ? [] : makeColumns(auditLogs);

  const onRowsPerPageChange = (rowsCount: number) => {
    setRowsPerPage(rowsCount);
    refetch({
      searchOptions: {
        first: rowsCount,
        sort: defaultSortOptions,
      },
    });
    setCurrentPage(1);
  };

  const onPageChange = React.useCallback(
    (newPage: number) => {
      setCurrentPage(newPage);
      refetch({
        searchOptions: {
          first: rowsPerPage,
          offset: (newPage - 1) * rowsPerPage,
          sort: defaultSortOptions,
        },
      });
    },
    [rowsPerPage, refetch]
  );

  return (
    <div className="audit-notes-v2-container" data-testid="audit-notes-txa-log">
      <CustomPanel
        testId="audit-trail-txa"
        heading={label}
        expandedHeader={`Collapse to hide ${panelHeading}`}
        collapsedHeader={`Expand to view ${panelHeading}`}
      >
        <div className="custom-design">
          <DynamicResultTable
            head={makeHead()}
            rows={columns}
            loadingSpinnerSize="small"
            totalCount={totalCount}
            emptyStateProps={getEmptyStateProps(error && error.toString())}
            isLoading={loading}
            isPaginationEnabled={totalCount > DEFAULT_ROWS_PER_PAGE}
            rowsPerPage={rowsPerPage}
            currentPage={currentPage}
            onPageChange={onPageChange}
            onRowChange={onRowsPerPageChange}
          ></DynamicResultTable>
        </div>
      </CustomPanel>
    </div>
  );
};
